import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllRead,
  NOTIFICATION_CACHE_KEY,
  Notification,
} from "@/services/notification-services";
import { useUser } from "@/hooks/useUser/useUser";

const UNREAD_COUNT_KEY = `${NOTIFICATION_CACHE_KEY}/unread-count`;
const POLL_INTERVAL_MS = 30_000;

export function useNotifications(drawerOpen: boolean) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const isClient = user?.role === "client";

  const { data: unreadData } = useQuery({
    queryKey: [UNREAD_COUNT_KEY],
    queryFn: getUnreadCount,
    refetchInterval: POLL_INTERVAL_MS,
    enabled: !!user && isClient,
    staleTime: POLL_INTERVAL_MS,
  });

  const { data: notifications, isLoading: isLoadingNotifications } = useQuery({
    queryKey: [NOTIFICATION_CACHE_KEY],
    queryFn: () => getNotifications(1, 20),
    enabled: !!user && isClient && drawerOpen,
    staleTime: 10_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [NOTIFICATION_CACHE_KEY] });
    queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] });
  };

  const markAsReadMutation = useMutation({
    mutationFn: (id: number) => markAsRead(id),
    onSuccess: (_data, id) => {
      // Optimistically mark it as read in the cache
      queryClient.setQueryData<Notification[]>([NOTIFICATION_CACHE_KEY], (old = []) =>
        old.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
      );
      queryClient.setQueryData<{ count: number }>([UNREAD_COUNT_KEY], (old) =>
        old && old.count > 0 ? { count: old.count - 1 } : { count: 0 },
      );
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.setQueryData<Notification[]>([NOTIFICATION_CACHE_KEY], (old = []) =>
        old.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })),
      );
      queryClient.setQueryData<{ count: number }>([UNREAD_COUNT_KEY], { count: 0 });
    },
  });

  return {
    unreadCount: unreadData?.count ?? 0,
    notifications: notifications ?? [],
    isLoadingNotifications,
    markAsRead: markAsReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
    invalidate,
  };
}
